import { useState, useEffect, useCallback, useRef } from "react";
import {
  Typography, Card, Row, Col, Button, Drawer, Form, Input, Select,
  DatePicker, Divider, Upload, message, Table, Tag, Popconfirm,
  Space, Switch,
} from "antd";
import {
  FileTextOutlined, CarOutlined, HomeOutlined, TeamOutlined,
  UploadOutlined, SearchOutlined, DownloadOutlined, DeleteOutlined,
  PlusOutlined, FileDoneOutlined, LoadingOutlined, UserOutlined, CloseOutlined,
  MailOutlined, AuditOutlined, TruckOutlined, ShoppingOutlined,
  FileProtectOutlined, FileOutlined, TagOutlined, CalendarOutlined,
  BankOutlined, IdcardOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { generateLegalDocument, getLegalDocuments, deleteLegalDocument } from "../../api/legalDocumentService";
import service from "../../api/fetchInterceptor";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  blue:   "#1d6a9e",
  green:  "#1d6a4f",
  amber:  "#b45309",
  purple: "#5a3a8a",
  red:    "#b91c1c",
  gray:   "#4b5563",
};

// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard = ({ n, title, icon, accent = C.blue, children, style }) => (
  <div style={{
    background: "white", borderRadius: 10,
    border: "1px solid #e8e8e8", borderLeft: `4px solid ${accent}`,
    padding: "14px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    ...style,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f0f0f0" }}>
      {n !== undefined && (
        <div style={{ width: 24, height: 24, borderRadius: "50%", background: accent, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
          {n}
        </div>
      )}
      {icon && <span style={{ color: accent, display: "flex", fontSize: 15 }}>{icon}</span>}
      <Text strong style={{ fontSize: 13, color: "#111" }}>{title}</Text>
    </div>
    {children}
  </div>
);

// ─── SubtypeGrid ──────────────────────────────────────────────────────────────
const SubtypeGrid = ({ options, value, onChange, cols = 3 }) => (
  <Row gutter={[8, 8]}>
    {options.map(o => {
      const sel = value === o.value;
      return (
        <Col key={o.value} xs={12} sm={Math.floor(24 / Math.min(cols, options.length))} md={Math.floor(24 / Math.min(cols + 1, options.length))}>
          <div onClick={() => onChange(o.value)} style={{
            borderRadius: 8, border: `2px solid ${sel ? C.blue : "#ddd"}`,
            background: sel ? C.blue : "white",
            padding: "8px 10px", cursor: "pointer", textAlign: "center",
            transition: "all 0.15s", userSelect: "none",
          }}>
            <Text style={{ fontSize: 11, fontWeight: 600, color: sel ? "white" : "#555", lineHeight: 1.35, display: "block" }}>
              {o.label}
            </Text>
          </div>
        </Col>
      );
    })}
  </Row>
);

// ─── PersonBlock ──────────────────────────────────────────────────────────────
const PersonBlock = ({ n, title, prefix, form, accent = C.blue, required = true, extra }) => {
  const [loading, setLoading] = useState(false);
  const lookup = async () => {
    const rut = form.getFieldValue(`${prefix}Rut`);
    if (!rut) return;
    setLoading(true);
    try {
      const { data } = await service.get(`/parse-docs/ci-rutificador?rut=${rut.replace(/\./g, '').trim()}`);
      if (data) {
        const f = {};
        if (data.name) f[`${prefix}Name`] = data.name;
        if (data.firstLastName) f[`${prefix}FirstLastName`] = data.firstLastName;
        if (data.secondLastName) f[`${prefix}SecondLastName`] = data.secondLastName;
        if (data.address) f[`${prefix}Address`] = data.address;
        if (data.commune) f[`${prefix}Commune`] = data.commune;
        form.setFieldsValue(f);
        message.success("Datos encontrados");
      } else { message.info("RUT no encontrado"); }
    } catch { message.warning("Sin resultados para ese RUT"); }
    finally { setLoading(false); }
  };
  return (
    <SectionCard n={n} title={title} icon={<UserOutlined />} accent={accent}>
      <Form.Item name={`${prefix}Rut`} style={{ marginBottom: 10 }} rules={required ? [{ required: true, message: "Requerido" }] : []}>
        <Input.Search placeholder="12.345.678-9 — Ingrese RUT y presione Buscar"
          loading={loading} enterButton={<><SearchOutlined /> Buscar</>}
          onSearch={lookup} style={{ maxWidth: 380 }} />
      </Form.Item>
      <Row gutter={[10, 0]}>
        <Col xs={24} sm={8}>
          <Form.Item label="Nombre" name={`${prefix}Name`} style={{ marginBottom: 8 }} rules={required ? [{ required: true }] : []}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label="Ap. Paterno" name={`${prefix}FirstLastName`} style={{ marginBottom: 8 }} rules={required ? [{ required: true }] : []}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label="Ap. Materno" name={`${prefix}SecondLastName`} style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={17}>
          <Form.Item label="Domicilio" name={`${prefix}Address`} style={{ marginBottom: 0 }} rules={required ? [{ required: true }] : []}>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} sm={7}>
          <Form.Item label="Comuna" name={`${prefix}Commune`} style={{ marginBottom: 0 }}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      {extra}
    </SectionCard>
  );
};

// ─── VehicleBlock ─────────────────────────────────────────────────────────────
const VehicleBlock = ({ n, form, onParsePDF, parsing }) => (
  <SectionCard n={n} title="Datos del Vehículo" icon={<CarOutlined />} accent={C.purple}>
    {onParsePDF && (
      <div style={{ marginBottom: 12 }}>
        <Upload beforeUpload={onParsePDF} showUploadList={false} accept=".pdf">
          <Button size="small" icon={parsing ? <LoadingOutlined /> : <UploadOutlined />} loading={parsing} type="dashed">
            Autocompletar desde PDF (Registro Civil)
          </Button>
        </Upload>
      </div>
    )}
    <Row gutter={[10, 0]}>
      {[
        { name: "inscription", label: "PPU / Inscripción", sm: 5 }, { name: "vehicleType", label: "Tipo", sm: 5 },
        { name: "make", label: "Marca", sm: 5 }, { name: "model", label: "Modelo", sm: 5 },
        { name: "vehicleYear", label: "Año", sm: 4 },
        { name: "motor", label: "N° Motor", sm: 6 }, { name: "chasis", label: "N° Chasis", sm: 6 },
        { name: "vin", label: "N° VIN", sm: 5 }, { name: "color", label: "Color", sm: 4 },
        { name: "gas", label: "Combustible", sm: 5 }, { name: "pbv", label: "PBV (kg)", sm: 4 },
      ].map(({ name, label, sm }) => (
        <Col key={name} xs={12} sm={sm}>
          <Form.Item label={label} name={name} style={{ marginBottom: 8 }}>
            <Input size="small" />
          </Form.Item>
        </Col>
      ))}
    </Row>
  </SectionCard>
);

// ─── ConditionsCard ───────────────────────────────────────────────────────────
const ConditionsCard = ({ n, title = "Condiciones y Fecha", children }) => (
  <SectionCard n={n} title={title} icon={<CalendarOutlined />} accent={C.gray}>
    {children}
  </SectionCard>
);

// ─── DJ Subtypes ──────────────────────────────────────────────────────────────
const DJ_SUBTYPES = [
  { value: "libre", label: "Declaración Libre", text: "" },
  { value: "no-inmueble", label: "No Posee Inmueble",
    text: "QUE, A LA FECHA DEL PRESENTE INSTRUMENTO, NO SOY PROPIETARIO/A DE NINGÚN BIEN INMUEBLE EN CHILE, SEGÚN LOS REGISTROS DEL CONSERVADOR DE BIENES RAÍCES Y/O EL SERVICIO DE IMPUESTOS INTERNOS." },
  { value: "accidente-vehiculo", label: "Accidente / Siniestro Vehículo",
    text: "QUE HE RECIBIDO LA SUMA DE $ ____________ (____________ PESOS), A ENTERA SATISFACCIÓN Y EN FORMA TOTAL E ÍNTEGRA, COMO PAGO DEFINITIVO POR LOS DAÑOS SUFRIDOS POR EL VEHÍCULO PATENTE ____________, CON MOTIVO DE ACCIDENTE/SINIESTRO OCURRIDO EL DÍA ____________. DECLARO QUE NADA MÁS TENGO QUE RECLAMAR POR ESTE CONCEPTO Y OTORGO EL MÁS AMPLIO Y TOTAL FINIQUITO." },
  { value: "vehiculo-atrasado", label: "Permiso de Circulación Atrasado",
    text: "QUE SOY PROPIETARIO/A DEL VEHÍCULO MOTORIZADO PATENTE ____________, Y QUE DICHO VEHÍCULO SE ENCUENTRA CON EL PERMISO DE CIRCULACIÓN CON FECHA VENCIDA/ATRASADA POR CAUSA JUSTIFICADA, SIENDO DE MI EXCLUSIVA RESPONSABILIDAD REGULARIZAR DICHA SITUACIÓN ANTE LA AUTORIDAD COMPETENTE EN EL MENOR TIEMPO POSIBLE." },
  { value: "no-sociedades", label: "No Participa en Sociedades",
    text: "QUE A LA FECHA DE SUSCRIPCIÓN DEL PRESENTE INSTRUMENTO, NO TENGO PARTICIPACIÓN EN SOCIEDAD O EMPRESA ALGUNA, YA SEA COMO SOCIO, ACCIONISTA, ADMINISTRADOR/A, DIRECTOR/A O EN CUALQUIER OTRA CALIDAD, EN CHILE O EN EL EXTRANJERO." },
  { value: "baja-vehiculo", label: "Dar de Baja Vehículo / Chatarra",
    text: "QUE SOY PROPIETARIO/A DEL VEHÍCULO MOTORIZADO PATENTE ____________, MARCA ____________, MODELO ____________, AÑO ____________, EL CUAL SE ENCUENTRA EN CONDICIÓN DE CHATARRA Y/O INMOVILIZADO. POR LO ANTERIOR, SOLICITO SU DAR DE BAJA DEFINITIVA ANTE EL REGISTRO CIVIL E IDENTIFICACIÓN, DECLARANDO QUE DICHO VEHÍCULO NO VOLVERÁ A CIRCULAR POR LAS VÍAS PÚBLICAS." },
];

// ─── Category Groups ──────────────────────────────────────────────────────────
const CATEGORY_GROUPS = [
  {
    group: "Vehículos y Transporte",
    categories: [
      { key: "poder-venta-vehiculo", label: "Poder\nVenta Vehículo",
        icon: <CarOutlined style={{ fontSize: 28, color: C.blue }} />,
        color: "#e8f4fd", border: "#b8d9f0", description: "Poder especial para vender un vehículo" },
      { key: "compraventa-especie", label: "Compraventa\nEspecie",
        icon: <ShoppingOutlined style={{ fontSize: 28, color: C.green }} />,
        color: "#edf7ee", border: "#a8d8b3", description: "Bote o especie usada",
        subtypes: [{ value: "bote", label: "Bote" }, { value: "especie-usada", label: "Especie Usada" }] },
      { key: "promesa-compraventa", label: "Promesa\nCompraventa",
        icon: <FileProtectOutlined style={{ fontSize: 28, color: C.purple }} />,
        color: "#f3eefb", border: "#c9b3e8", description: "Promesa de compraventa de vehículo" },
      { key: "salvo-conducto-mudanza", label: "Salvo Conducto\nMudanza",
        icon: <TruckOutlined style={{ fontSize: 28, color: C.amber }} />,
        color: "#fdf6e8", border: "#e8d5a3", description: "Traslado de efectos personales" },
      { key: "no-registra-tag", label: "No Registra\nTAG",
        icon: <TagOutlined style={{ fontSize: 28, color: C.red }} />,
        color: "#fdecea", border: "#f5b3b3", description: "No registro en sistema TAG" },
    ],
  },
  {
    group: "Contratos",
    categories: [
      { key: "contrato-arriendo", label: "Contrato de\nArriendo",
        icon: <HomeOutlined style={{ fontSize: 28, color: C.blue }} />,
        color: "#e8f4fd", border: "#b8d9f0", description: "Arrendamiento de bien inmueble" },
      { key: "comodato", label: "Contrato de\nComodato",
        icon: <TeamOutlined style={{ fontSize: 28, color: C.blue }} />,
        color: "#e8f4fd", border: "#b8d9f0", description: "Préstamo de uso gratuito de un bien" },
    ],
  },
  {
    group: "Autorizaciones Menores de Edad",
    categories: [
      { key: "autorizacion-menor", label: "Autorización\nMenor de Edad",
        icon: <UserOutlined style={{ fontSize: 28, color: C.amber }} />,
        color: "#fdf6e8", border: "#e8d5a3", description: "Actividades de menores de edad",
        subtypes: [
          { value: "practica", label: "Práctica Laboral" }, { value: "conduccion", label: "Curso Conducción" },
          { value: "licencia-conducir", label: "Licencia Conducir" }, { value: "carabineros", label: "Escuela Carabineros" },
          { value: "trabajo", label: "Para Trabajar" }, { value: "actividad-especial", label: "Actividad Especial" },
        ] },
    ],
  },
  {
    group: "Poderes y Mandatos",
    categories: [
      { key: "poder-simple", label: "Poder\nSimple",
        icon: <FileOutlined style={{ fontSize: 28, color: C.green }} />,
        color: "#edf7ee", border: "#a8d8b3", description: "Gestiones personales diversas",
        subtypes: [
          { value: "retiro-cedula", label: "Retiro / Gestión" }, { value: "fondos-fallecido", label: "Fondos Fallecido" },
          { value: "gestion-vehiculo", label: "Gestiones Vehículo" }, { value: "herencia-banco-estado", label: "Autorización Cónyuge" },
          { value: "licencia-medica", label: "Cobro Licencia Médica" }, { value: "admin-inmuebles", label: "Adm. Inmuebles" },
        ] },
      { key: "poder-contador", label: "Mandato\nContador",
        icon: <AuditOutlined style={{ fontSize: 28, color: C.purple }} />,
        color: "#f3eefb", border: "#c9b3e8", description: "Ante SII, Tesorería, AFP, AFC, ISL" },
    ],
  },
  {
    group: "Declaraciones Juradas",
    categories: [
      { key: "declaracion-jurada", label: "Declaración\nJurada",
        icon: <FileDoneOutlined style={{ fontSize: 28, color: C.green }} />,
        color: "#e8f5e9", border: "#b2dfdb", description: "Declaración ante notario",
        subtypes: DJ_SUBTYPES.map(s => ({ value: s.value, label: s.label })) },
    ],
  },
  {
    group: "Cartas Notariales",
    categories: [
      { key: "carta-notarial", label: "Carta\nNotarial",
        icon: <MailOutlined style={{ fontSize: 28, color: C.red }} />,
        color: "#fdecea", border: "#f5b3b3", description: "Aviso, responsabilidad o renuncia",
        subtypes: [
          { value: "renuncia", label: "Carta de Renuncia" },
          { value: "responsabilidad", label: "Responsabilidad Vehículo" },
          { value: "aviso-termino", label: "Aviso Término Arriendo" },
        ] },
    ],
  },
];

const VIGENCIA_OPTIONS = [
  { value: "30 días", label: "30 días" }, { value: "60 días", label: "60 días" },
  { value: "90 días", label: "90 días" }, { value: "6 meses", label: "6 meses" },
  { value: "1 año", label: "1 año (máximo)" },
];

// ─── Form: Poder Venta Vehículo ───────────────────────────────────────────────
const PoderVentaVehiculoForm = ({ form }) => {
  const [parsing, setParsing] = useState(false);
  const handleBg = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await service.post("/parse-docs/parse-vehicle-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const v = data || {};
      form.setFieldsValue({ inscription: v.inscription || "", vehicleType: v.type || "", make: v.make || "", model: v.model || "", vehicleYear: String(v.year || ""), motor: v.motor || "", chasis: v.chasis || "", vin: v.vin || "", color: v.color || "", gas: v.gas || "" });
      message.success("Datos extraídos");
    } catch { message.error("No se pudo leer el PDF"); }
    finally { setParsing(false); }
    return false;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PersonBlock n={1} title="Poderdante — quien otorga el poder" prefix="mandante" form={form} accent={C.blue} />
      <PersonBlock n={2} title="Apoderado — quien recibe el poder" prefix="mandatary" form={form} accent={C.green} />
      <VehicleBlock n={3} form={form} onParsePDF={handleBg} parsing={parsing} />
      <ConditionsCard n={4} title="Condiciones del Poder">
        <Row gutter={12}>
          <Col xs={24} sm={10}>
            <Form.Item label="Vigencia del poder" name="duration" rules={[{ required: true, message: "Requerida" }]}>
              <Select placeholder="Selecciona vigencia" getPopupContainer={t => t.parentNode}>
                {VIGENCIA_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Fecha del documento" name="date" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Declaración Jurada ─────────────────────────────────────────────────
const DeclaracionJuradaForm = ({ form }) => {
  const [subtype, setSubtype] = useState("libre");
  const handleChange = (v) => {
    setSubtype(v);
    form.setFieldValue("subtype", v);
    const sub = DJ_SUBTYPES.find(s => s.value === v);
    if (sub) form.setFieldValue("text", sub.text);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Declaración" icon={<FileDoneOutlined />} accent={C.green}>
        <Form.Item name="subtype" hidden initialValue="libre"><Input /></Form.Item>
        <SubtypeGrid options={DJ_SUBTYPES} value={subtype} onChange={handleChange} cols={3} />
      </SectionCard>
      <PersonBlock n={2} title="Compareciente" prefix="declarant" form={form} accent={C.blue} />
      <SectionCard n={3} title="Texto de la Declaración" icon={<FileTextOutlined />} accent={C.gray}>
        <Form.Item name="text" rules={[{ required: true, message: "Requerido" }]} style={{ marginBottom: 10 }}>
          <TextArea rows={7} placeholder="Ingrese el texto completo de la declaración jurada..." />
        </Form.Item>
        <Form.Item label="Fecha" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
          <DatePicker style={{ width: 200 }} format="DD/MM/YYYY" />
        </Form.Item>
      </SectionCard>
    </div>
  );
};

// ─── Form: Contrato Arriendo ──────────────────────────────────────────────────
const ContratoArriendoForm = ({ form }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <PersonBlock n={1} title="Arrendador — propietario del inmueble" prefix="landlord" form={form} accent={C.blue} />
    <PersonBlock n={2} title="Arrendatario — quien arrienda" prefix="tenant" form={form} accent={C.green} />
    <SectionCard n={3} title="Inmueble y Condiciones" icon={<HomeOutlined />} accent={C.purple}>
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={17}><Form.Item label="Dirección del inmueble" name="propertyAddress" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={24} sm={7}><Form.Item label="Comuna" name="propertyCommune" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={12} sm={6}><Form.Item label="Renta mensual ($)" name="monthlyRent" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
        <Col xs={12} sm={6}><Form.Item label="Duración" name="duration" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input placeholder="12 meses" /></Form.Item></Col>
        <Col xs={12} sm={6}><Form.Item label="Fecha de inicio" name="startDate" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
      </Row>
    </SectionCard>
  </div>
);

// ─── Form: Comodato ───────────────────────────────────────────────────────────
const ComodatoForm = ({ form }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <PersonBlock n={1} title="Comodante — quien presta el bien" prefix="lender" form={form} accent={C.blue} />
    <PersonBlock n={2} title="Comodatario — quien recibe el bien" prefix="borrower" form={form} accent={C.amber} />
    <SectionCard n={3} title="Bien en Comodato" icon={<TeamOutlined />} accent={C.purple}>
      <Form.Item label="Descripción del bien" name="assetDescription" rules={[{ required: true }]} style={{ marginBottom: 10 }}>
        <TextArea rows={3} placeholder="Describa el bien entregado en comodato..." />
      </Form.Item>
      <Row gutter={12}>
        <Col xs={12} sm={8}><Form.Item label="Plazo" name="duration" rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input placeholder="Indefinido / 6 meses" /></Form.Item></Col>
        <Col xs={12} sm={8}><Form.Item label="Fecha" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
      </Row>
    </SectionCard>
  </div>
);

// ─── Form: Autorización Menor de Edad ────────────────────────────────────────
const MINOR_SUBTYPES = [
  { value: "practica", label: "Práctica Laboral" }, { value: "conduccion", label: "Curso Conducción" },
  { value: "licencia-conducir", label: "Licencia Conducir" }, { value: "carabineros", label: "Escuela Carabineros" },
  { value: "trabajo", label: "Para Trabajar" }, { value: "actividad-especial", label: "Actividad Especial" },
];
const AutorizacionMenorForm = ({ form }) => {
  const [subtype, setSubtype] = useState("practica");
  const showParent2 = subtype === "carabineros" || subtype === "actividad-especial";
  const handle = (v) => { setSubtype(v); form.setFieldValue("subtype", v); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Autorización" icon={<IdcardOutlined />} accent={C.amber}>
        <Form.Item name="subtype" hidden initialValue="practica"><Input /></Form.Item>
        <SubtypeGrid options={MINOR_SUBTYPES} value={subtype} onChange={handle} cols={3} />
      </SectionCard>
      <PersonBlock n={2} title="Padre / Madre / Tutor" prefix="parent1" form={form} accent={C.blue}
        extra={
          <Row gutter={10} style={{ marginTop: 8 }}>
            <Col xs={12} sm={8}>
              <Form.Item label="Calidad" name="parent1Role" style={{ marginBottom: 0 }}>
                <Input placeholder="padre / madre / tutor" />
              </Form.Item>
            </Col>
          </Row>
        }
      />
      {showParent2 && (
        <PersonBlock n={3} title="Segundo Padre / Madre" prefix="parent2" form={form} accent={C.amber} required={false} />
      )}
      <PersonBlock n={showParent2 ? 4 : 3} title="Menor de Edad" prefix="minor" form={form} accent={C.green} />
      {(subtype === "practica") && (
        <ConditionsCard n={showParent2 ? 5 : 4} title="Datos de la Práctica">
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item label="Establecimiento educacional" name="institution" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Especialidad" name="specialty" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </ConditionsCard>
      )}
      {(subtype === "conduccion" || subtype === "licencia-conducir") && (
        <ConditionsCard n={4} title="Datos del Curso">
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item label="Escuela de conductores" name="drivingSchool" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={12} sm={6}><Form.Item label="Clase licencia" name="licenseClass" initialValue="B" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </ConditionsCard>
      )}
      {subtype === "actividad-especial" && (
        <ConditionsCard n={5} title="Datos de la Actividad">
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item label="Organización" name="organization" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item label="Actividad" name="activity" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </ConditionsCard>
      )}
      <ConditionsCard n="·" title="Fecha del documento">
        <Form.Item name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
          <DatePicker style={{ width: 220 }} format="DD/MM/YYYY" />
        </Form.Item>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Poder Simple ───────────────────────────────────────────────────────
const PODER_SUBTYPES = [
  { value: "retiro-cedula", label: "Retiro / Gestión Personal" },
  { value: "fondos-fallecido", label: "Fondos Persona Fallecida" },
  { value: "gestion-vehiculo", label: "Gestiones Vehículo" },
  { value: "herencia-banco-estado", label: "Autorización Cónyuge" },
  { value: "licencia-medica", label: "Cobro Licencia Médica" },
  { value: "admin-inmuebles", label: "Adm. Inmuebles" },
];
const PoderSimpleForm = ({ form }) => {
  const [subtype, setSubtype] = useState("retiro-cedula");
  const handle = (v) => { setSubtype(v); form.setFieldValue("subtype", v); };
  const showVehicle = subtype === "gestion-vehiculo";
  const showFallecido = subtype === "fondos-fallecido" || subtype === "licencia-medica";
  const isConyugue = subtype === "herencia-banco-estado";
  const showPurpose = subtype === "retiro-cedula" || subtype === "admin-inmuebles";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Poder" icon={<FileOutlined />} accent={C.green}>
        <Form.Item name="subtype" hidden initialValue="retiro-cedula"><Input /></Form.Item>
        <SubtypeGrid options={PODER_SUBTYPES} value={subtype} onChange={handle} cols={3} />
      </SectionCard>
      <PersonBlock n={2} title="Mandante — quien otorga el poder" prefix="mandante" form={form} accent={C.blue} />
      {!isConyugue && (
        <PersonBlock n={3} title="Mandatario — quien recibe el poder" prefix="mandatary" form={form} accent={C.green} />
      )}
      {isConyugue && (
        <>
          <PersonBlock n={3} title="Cónyuge autorizado/a" prefix="conyugue" form={form} accent={C.green} />
          <SectionCard n={4} title="Persona Fallecida" icon={<UserOutlined />} accent={C.red}>
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={9}><Form.Item label="Nombre" name="fallecidoName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={9}><Form.Item label="Ap. Paterno" name="fallecidoFirstLastName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={6}><Form.Item label="RUT" name="fallecidoRut" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item label="Institución (AFP, Banco...)" name="fallecidoInstitution" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            </Row>
          </SectionCard>
        </>
      )}
      {showFallecido && (
        <SectionCard n={4} title="Persona Fallecida" icon={<UserOutlined />} accent={C.red}>
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={8}><Form.Item label="Nombre" name="fallecidoName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Ap. Paterno" name="fallecidoFirstLastName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="RUT" name="fallecidoRut" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={9}><Form.Item label="Institución (banco/AFP)" name="fallecidoInstitution" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={12} sm={7}><Form.Item label="N° Cuenta" name="accountNumber" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={12} sm={8}><Form.Item label="Monto ($)" name="amount" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </SectionCard>
      )}
      {showVehicle && (
        <SectionCard n={4} title="Datos del Vehículo" icon={<CarOutlined />} accent={C.purple}>
          <Row gutter={[10, 0]}>
            {[
              { name: "inscription", label: "Inscripción" }, { name: "vehicleType", label: "Tipo" },
              { name: "make", label: "Marca" }, { name: "model", label: "Modelo" },
              { name: "vehicleYear", label: "Año" }, { name: "motor", label: "N° Motor" },
              { name: "chasis", label: "N° Chasis" }, { name: "color", label: "Color" },
            ].map(({ name, label }) => (
              <Col key={name} xs={12} sm={6}><Form.Item label={label} name={name} style={{ marginBottom: 8 }}><Input size="small" /></Form.Item></Col>
            ))}
            <Col xs={24} sm={8}><Form.Item label="Vigencia del poder" name="duration" style={{ marginBottom: 0 }}><Input placeholder="06 meses / 1 año" /></Form.Item></Col>
          </Row>
        </SectionCard>
      )}
      {showPurpose && (
        <ConditionsCard n={4} title="Propósito del Poder">
          <Form.Item name="purpose" style={{ marginBottom: 0 }}>
            <TextArea rows={3} placeholder="Describa qué debe hacer el mandatario..." />
          </Form.Item>
        </ConditionsCard>
      )}
      <ConditionsCard n="·" title="Fecha del documento">
        <Form.Item name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
          <DatePicker style={{ width: 220 }} format="DD/MM/YYYY" />
        </Form.Item>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Poder Contador ─────────────────────────────────────────────────────
const PoderContadorForm = ({ form }) => {
  const [isCompany, setIsCompany] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Mandante" icon={<BankOutlined />} accent={C.blue}>
        <Space>
          <Text style={{ color: "#555" }}>Tipo de mandante:</Text>
          <Switch
            checkedChildren="Empresa / Sociedad"
            unCheckedChildren="Persona Natural"
            checked={isCompany}
            onChange={(v) => { setIsCompany(v); form.setFieldValue("mandanteIsCompany", v); }}
          />
          <Form.Item name="mandanteIsCompany" hidden initialValue={false}><Input /></Form.Item>
        </Space>
      </SectionCard>
      {isCompany ? (
        <SectionCard n={2} title="Empresa Mandante" icon={<BankOutlined />} accent={C.blue}>
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={14}><Form.Item label="Razón social" name="mandanteCompanyName" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={10}><Form.Item label="RUT empresa" name="mandanteCompanyRut" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Rep. Legal — Nombre" name="mandanteRepName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="Ap. Paterno" name="mandanteRepFirstLastName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={8}><Form.Item label="RUT representante" name="mandanteRepRut" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={17}><Form.Item label="Domicilio empresa" name="mandanteAddress" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={24} sm={7}><Form.Item label="Comuna" name="mandanteCommune" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </SectionCard>
      ) : (
        <PersonBlock n={2} title="Mandante — quien da el poder" prefix="mandante" form={form} accent={C.blue} />
      )}
      <PersonBlock n={3} title="Contador — quien recibe el mandato" prefix="mandatary" form={form} accent={C.green} />
      <ConditionsCard n={4} title="Entidades y Fecha">
        <Form.Item label="Entidades (separadas por coma)" name="entities" initialValue="SII,TESORERIA,INSPECCION_TRABAJO,AFC,ISL,AFP" style={{ marginBottom: 10 }}>
          <Input placeholder="SII, TESORERIA, AFP, AFC, ISL, INSPECCION_TRABAJO" />
        </Form.Item>
        <Form.Item label="Fecha" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
          <DatePicker style={{ width: 220 }} format="DD/MM/YYYY" />
        </Form.Item>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Compraventa Especie ────────────────────────────────────────────────
const CompraventaEspecieForm = ({ form }) => {
  const [subtype, setSubtype] = useState("especie-usada");
  const handle = (v) => { setSubtype(v); form.setFieldValue("subtype", v); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Compraventa" icon={<ShoppingOutlined />} accent={C.green}>
        <Form.Item name="subtype" hidden initialValue="especie-usada"><Input /></Form.Item>
        <SubtypeGrid options={[{ value: "especie-usada", label: "Especie / Equipo Usado" }, { value: "bote", label: "Bote / Embarcación" }]} value={subtype} onChange={handle} cols={2} />
      </SectionCard>
      <PersonBlock n={2} title="Vendedor" prefix="seller" form={form} accent={C.blue} />
      <PersonBlock n={3} title="Comprador" prefix="buyer" form={form} accent={C.green} />
      {subtype === "bote" ? (
        <SectionCard n={4} title="Datos del Bote" icon={<FileProtectOutlined />} accent={C.purple}>
          <Row gutter={[10, 0]}>
            <Col xs={24} sm={14}><Form.Item label="Nombre del bote" name="boatName" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
            <Col xs={24}><Form.Item label="Descripción (tipo, color, casco...)" name="boatDescription" rules={[{ required: true }]} style={{ marginBottom: 8 }}><TextArea rows={2} /></Form.Item></Col>
            <Col xs={8}><Form.Item label="Eslora" name="boatSlora" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={8}><Form.Item label="Manga" name="boatManga" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            <Col xs={8}><Form.Item label="Puntal" name="boatPuntal" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          </Row>
        </SectionCard>
      ) : (
        <SectionCard n={4} title="Descripción de la Especie" icon={<FileTextOutlined />} accent={C.purple}>
          <Form.Item name="itemDescription" rules={[{ required: true }]} style={{ marginBottom: 8 }}>
            <TextArea rows={4} placeholder="Describa detalladamente la especie: características, marca, modelo, estado..." />
          </Form.Item>
          <Form.Item label="Fecha de entrega material" name="itemDeliveryDate" style={{ marginBottom: 0 }}>
            <DatePicker style={{ width: 220 }} format="DD/MM/YYYY" />
          </Form.Item>
        </SectionCard>
      )}
      <ConditionsCard n={5} title="Precio y Forma de Pago">
        <Row gutter={12}>
          <Col xs={12} sm={6}><Form.Item label="Precio ($)" name="price" rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={8}><Form.Item label="Forma de pago" name="paymentMethod" initialValue="EFECTIVO" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item label="Fecha" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
        </Row>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Promesa Compraventa ────────────────────────────────────────────────
const PromesaCompraventaForm = ({ form }) => {
  const [parsing, setParsing] = useState(false);
  const handleBg = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await service.post("/parse-docs/parse-vehicle-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const v = data || {};
      form.setFieldsValue({ inscription: v.inscription || "", vehicleType: v.type || "", make: v.make || "", model: v.model || "", vehicleYear: String(v.year || ""), motor: v.motor || "", chasis: v.chasis || "", vin: v.vin || "", color: v.color || "", gas: v.gas || "", pbv: v.pbv || "" });
      message.success("Datos extraídos");
    } catch { message.error("No se pudo leer el PDF"); }
    finally { setParsing(false); }
    return false;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PersonBlock n={1} title="Promitente Vendedor" prefix="seller" form={form} accent={C.blue} />
      <PersonBlock n={2} title="Promitente Comprador" prefix="buyer" form={form} accent={C.green} />
      <VehicleBlock n={3} form={form} onParsePDF={handleBg} parsing={parsing} />
      <ConditionsCard n={4} title="Precio y Plan de Pago">
        <Row gutter={[12, 0]}>
          <Col xs={12} sm={6}><Form.Item label="Precio total ($)" name="totalPrice" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={6}><Form.Item label="Monto ya pagado ($)" name="paidAmount" style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={6}><Form.Item label="Entrega al firmar ($)" name="entryAmount" style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={6}><Form.Item label="Saldo ($)" name="balanceAmount" style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={4}><Form.Item label="N° cuotas" name="installments" style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={12} sm={6}><Form.Item label="Valor cuota ($)" name="installmentAmount" style={{ marginBottom: 8 }}><Input type="number" /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item label="Fecha del contrato" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
        </Row>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Salvo Conducto Mudanza ─────────────────────────────────────────────
const SalvoConductoMudanzaForm = ({ form }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    <PersonBlock n={1} title="Declarante" prefix="declarant" form={form} accent={C.blue} />
    <SectionCard n={2} title="Datos de la Mudanza" icon={<TruckOutlined />} accent={C.amber}>
      <Row gutter={[12, 0]}>
        <Col xs={24} sm={8}><Form.Item label="Fecha de traslado" name="movingDate" rules={[{ required: true }]} style={{ marginBottom: 8 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
        <Col xs={24}><Form.Item label="Dirección de destino" name="destinationAddress" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={12} sm={5}><Form.Item label="Tipo vehículo" name="vehicleType" initialValue="CAMIÓN" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={12} sm={5}><Form.Item label="Patente" name="vehiclePlate" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={12} sm={7}><Form.Item label="Nombre conductor" name="driverName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={12} sm={7}><Form.Item label="RUT conductor" name="driverRut" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
        <Col xs={24}><Form.Item label="Dónde se presentará el documento" name="purpose" style={{ marginBottom: 8 }}><Input placeholder="aseguradora / donde se estime conveniente" /></Form.Item></Col>
        <Col xs={24} sm={8}><Form.Item label="Fecha del documento" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
      </Row>
    </SectionCard>
  </div>
);

// ─── Form: No Registra TAG ────────────────────────────────────────────────────
const NoRegistraTagForm = ({ form }) => {
  const [parsing, setParsing] = useState(false);
  const handleBg = async (file) => {
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await service.post("/parse-docs/parse-vehicle-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const v = data || {};
      form.setFieldsValue({ vehicleType: v.type || "", make: v.make || "", model: v.model || "", vehicleYear: String(v.year || ""), color: v.color || "", motor: v.motor || "", chasis: v.chasis || "", inscription: v.inscription || "" });
      message.success("Datos extraídos");
    } catch { message.error("No se pudo leer el PDF"); }
    finally { setParsing(false); }
    return false;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <PersonBlock n={1} title="Declarante" prefix="declarant" form={form} accent={C.blue} />
      <SectionCard n={2} title="Datos del Vehículo" icon={<CarOutlined />} accent={C.purple}>
        <div style={{ marginBottom: 10 }}>
          <Upload beforeUpload={handleBg} showUploadList={false} accept=".pdf">
            <Button size="small" icon={parsing ? <LoadingOutlined /> : <UploadOutlined />} loading={parsing} type="dashed">
              Autocompletar desde PDF (Registro Civil)
            </Button>
          </Upload>
        </div>
        <Row gutter={[10, 0]}>
          {[
            { name: "vehicleType", label: "Tipo", sm: 6 }, { name: "make", label: "Marca", sm: 6 },
            { name: "model", label: "Modelo", sm: 6 }, { name: "vehicleYear", label: "Año", sm: 6 },
            { name: "color", label: "Color", sm: 6 }, { name: "motor", label: "N° Motor", sm: 6 },
            { name: "chasis", label: "N° Chasis", sm: 6 }, { name: "inscription", label: "Inscripción (PPU)", sm: 6 },
          ].map(({ name, label, sm }) => (
            <Col key={name} xs={12} sm={sm}><Form.Item label={label} name={name} style={{ marginBottom: 8 }}><Input size="small" /></Form.Item></Col>
          ))}
        </Row>
      </SectionCard>
      <ConditionsCard n={3} title="Datos del Documento">
        <Row gutter={12}>
          <Col xs={24} sm={12}><Form.Item label="Dónde se presentará" name="purpose" style={{ marginBottom: 0 }}><Input placeholder="aseguradora / municipalidad..." /></Form.Item></Col>
          <Col xs={24} sm={8}><Form.Item label="Fecha" name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
        </Row>
      </ConditionsCard>
    </div>
  );
};

// ─── Form: Carta Notarial ─────────────────────────────────────────────────────
const CARTA_SUBTYPES = [
  { value: "renuncia", label: "Carta de Renuncia" },
  { value: "responsabilidad", label: "Responsabilidad Vehículo" },
  { value: "aviso-termino", label: "Aviso Término Arrendatario" },
];
const CartaNotarialForm = ({ form }) => {
  const [subtype, setSubtype] = useState("renuncia");
  const handle = (v) => { setSubtype(v); form.setFieldValue("subtype", v); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionCard n={1} title="Tipo de Carta" icon={<MailOutlined />} accent={C.red}>
        <Form.Item name="subtype" hidden initialValue="renuncia"><Input /></Form.Item>
        <SubtypeGrid options={CARTA_SUBTYPES} value={subtype} onChange={handle} cols={3} />
      </SectionCard>

      {subtype === "renuncia" && (
        <>
          <PersonBlock n={2} title="Trabajador — quien renuncia" prefix="employee" form={form} accent={C.blue} />
          <SectionCard n={3} title="Empleador y Cargo" icon={<BankOutlined />} accent={C.gray}>
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={14}><Form.Item label="Nombre / Empresa" name="employerName" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={10}><Form.Item label="RUT empleador" name="employerRut" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24}><Form.Item label="Dirección empleador" name="employerAddress" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item label="Cargo que desempeña" name="position" rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={12}><Form.Item label="Fecha efectiva de renuncia" name="resignationDate" rules={[{ required: true }]} style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
            </Row>
          </SectionCard>
        </>
      )}

      {subtype === "responsabilidad" && (
        <>
          <PersonBlock n={2} title="Declarante — quien asume responsabilidad" prefix="declarant" form={form} accent={C.blue} />
          <SectionCard n={3} title="Datos del Vehículo" icon={<CarOutlined />} accent={C.purple}>
            <Row gutter={[10, 0]}>
              {[
                { name: "inscription", label: "Inscripción" }, { name: "vehicleType", label: "Tipo" },
                { name: "vehicleYear", label: "Año" }, { name: "make", label: "Marca" },
                { name: "model", label: "Modelo" }, { name: "motor", label: "N° Motor" },
                { name: "chasis", label: "N° Chasis" }, { name: "color", label: "Color" },
                { name: "gas", label: "Combustible" }, { name: "pbv", label: "PBV (kg)" },
              ].map(({ name, label }) => (
                <Col key={name} xs={12} sm={6}><Form.Item label={label} name={name} style={{ marginBottom: 8 }}><Input size="small" /></Form.Item></Col>
              ))}
            </Row>
          </SectionCard>
          <SectionCard n={4} title="Propietario del Vehículo" icon={<UserOutlined />} accent={C.amber}>
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={8}><Form.Item label="Nombre" name="ownerName" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Ap. Paterno" name="ownerFirstLastName" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="RUT propietario" name="ownerRut" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            </Row>
          </SectionCard>
        </>
      )}

      {subtype === "aviso-termino" && (
        <>
          <PersonBlock n={2} title="Arrendatario — quien da el aviso" prefix="tenant" form={form} accent={C.blue} />
          <SectionCard n={3} title="Propietario / Arrendador" icon={<HomeOutlined />} accent={C.amber}>
            <Row gutter={[10, 0]}>
              <Col xs={24} sm={8}><Form.Item label="Nombre" name="landlordName" rules={[{ required: true }]} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Ap. Paterno" name="landlordFirstLastName" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24}><Form.Item label="Dirección del arrendador" name="landlordAddress" style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
              <Col xs={24} sm={14}><Form.Item label="Dirección del inmueble arrendado" name="propertyAddress" rules={[{ required: true }]} style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
            </Row>
          </SectionCard>
          <ConditionsCard n={4} title="Datos del Contrato y Aviso">
            <Row gutter={[12, 0]}>
              <Col xs={12} sm={6}><Form.Item label="Fecha del contrato" name="contractDate" style={{ marginBottom: 0 }}><DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" /></Form.Item></Col>
              <Col xs={12} sm={8}><Form.Item label="Notaría del contrato" name="contractNotary" style={{ marginBottom: 0 }}><Input /></Form.Item></Col>
              <Col xs={12} sm={4}><Form.Item label="Días de aviso" name="noticeDays" initialValue={60} style={{ marginBottom: 0 }}><Input type="number" /></Form.Item></Col>
            </Row>
          </ConditionsCard>
        </>
      )}

      <ConditionsCard n="·" title="Fecha del documento">
        <Form.Item name="date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
          <DatePicker style={{ width: 220 }} format="DD/MM/YYYY" />
        </Form.Item>
      </ConditionsCard>
    </div>
  );
};

// ─── Forms map ────────────────────────────────────────────────────────────────
const FORMS = {
  "poder-venta-vehiculo": PoderVentaVehiculoForm,
  "declaracion-jurada": DeclaracionJuradaForm,
  "contrato-arriendo": ContratoArriendoForm,
  comodato: ComodatoForm,
  "autorizacion-menor": AutorizacionMenorForm,
  "poder-simple": PoderSimpleForm,
  "poder-contador": PoderContadorForm,
  "compraventa-especie": CompraventaEspecieForm,
  "promesa-compraventa": PromesaCompraventaForm,
  "salvo-conducto-mudanza": SalvoConductoMudanzaForm,
  "no-registra-tag": NoRegistraTagForm,
  "carta-notarial": CartaNotarialForm,
};

const TYPE_LABELS = {
  "poder-venta-vehiculo": "Poder Venta Vehículo", "declaracion-jurada": "Declaración Jurada",
  "contrato-arriendo": "Contrato Arriendo", comodato: "Comodato",
  "autorizacion-menor": "Autorización Menor", "poder-simple": "Poder Simple",
  "poder-contador": "Mandato Contador", "compraventa-especie": "Compraventa Especie",
  "promesa-compraventa": "Promesa Compraventa", "salvo-conducto-mudanza": "Salvo Conducto",
  "no-registra-tag": "No Registra TAG", "carta-notarial": "Carta Notarial",
};
const TYPE_COLORS = {
  "poder-venta-vehiculo": "blue", "declaracion-jurada": "green",
  "contrato-arriendo": "purple", comodato: "orange",
  "autorizacion-menor": "gold", "poder-simple": "cyan",
  "poder-contador": "volcano", "compraventa-especie": "lime",
  "promesa-compraventa": "geekblue", "salvo-conducto-mudanza": "magenta",
  "no-registra-tag": "red", "carta-notarial": "pink",
};

const serializeFormData = (values) => {
  const out = {};
  for (const [k, v] of Object.entries(values)) {
    if (v && typeof v === "object" && v.$d) out[k] = v.toISOString();
    else out[k] = v;
  }
  return out;
};

// ─── History Table ────────────────────────────────────────────────────────────
const getParty1 = (r) => {
  const d = r.data || {};
  const name = d.mandanteName || d.declarantName || d.landlordName || d.lenderName || d.sellerName || d.parent1Name || d.employeeName || d.tenantName || '';
  const ap = d.mandanteFirstLastName || d.declarantFirstLastName || d.landlordFirstLastName || d.lenderFirstLastName || d.sellerFirstLastName || d.parent1FirstLastName || d.employeeFirstLastName || d.tenantFirstLastName || '';
  return [name, ap].filter(Boolean).join(' ') || '—';
};
const getParty2 = (r) => {
  const d = r.data || {};
  const name = d.mandataryName || d.tenantName || d.borrowerName || d.buyerName || d.minorName || '';
  const ap = d.mandataryFirstLastName || d.tenantFirstLastName || d.borrowerFirstLastName || d.buyerFirstLastName || d.minorFirstLastName || '';
  return [name, ap].filter(Boolean).join(' ') || '—';
};

const HistoryTable = ({ refresh }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(null);
  const [filterDates, setFilterDates] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;
  const debounceRef = useRef(null);

  const load = useCallback((p = 1) => {
    setLoading(true);
    const params = { page: p, limit: PAGE_SIZE };
    if (search) params.search = search;
    if (filterType) params.type = filterType;
    if (filterDates?.[0]) params.dateFrom = filterDates[0].startOf('day').toISOString();
    if (filterDates?.[1]) params.dateTo = filterDates[1].endOf('day').toISOString();
    getLegalDocuments(params)
      .then(({ data }) => { setDocs(data.results || []); setTotal(data.totalResults || 0); setPage(p); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, filterType, filterDates]);

  useEffect(() => { load(1); }, [refresh, filterType, filterDates, load]);

  const handleDelete = async (id) => {
    try { await deleteLegalDocument(id); message.success("Eliminado"); load(); }
    catch { message.error("Error al eliminar"); }
  };

  const columns = [
    { title: "Tipo", dataIndex: "type", width: 160,
      render: v => <Tag color={TYPE_COLORS[v] || "default"} style={{ fontSize: 11 }}>{TYPE_LABELS[v] || v}</Tag> },
    { title: "Parte 1", key: "p1", render: (_, r) => <Text style={{ fontSize: 12 }}>{getParty1(r)}</Text> },
    { title: "Parte 2", key: "p2", render: (_, r) => <Text style={{ fontSize: 12 }}>{getParty2(r)}</Text> },
    { title: "Fecha", dataIndex: "createdAt", width: 120,
      render: v => v ? <Text style={{ fontSize: 12 }}>{dayjs(v).format("DD/MM/YYYY HH:mm")}</Text> : "—" },
    { title: "Descargar", key: "actions", width: 200,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {r.pdfUrl && <Button size="small" href={r.pdfUrl} target="_blank" style={{ background: "#c0392b", borderColor: "#c0392b", color: "white", fontSize: 11 }} icon={<DownloadOutlined />}>PDF</Button>}
          {r.fileUrl && <Button size="small" href={r.fileUrl} target="_blank" style={{ background: "#1d3557", borderColor: "#1d3557", color: "white", fontSize: 11 }} icon={<DownloadOutlined />}>Word</Button>}
          <Divider type="vertical" style={{ margin: "0 4px" }} />
          <Popconfirm title="¿Eliminar este documento?" onConfirm={() => handleDelete(r.id)} okText="Sí" cancelText="No">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ) },
  ];

  return (
    <>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={10}>
          <Input.Search placeholder="Buscar por nombre, RUT..." allowClear value={search}
            onChange={(e) => { setSearch(e.target.value); clearTimeout(debounceRef.current); debounceRef.current = setTimeout(() => load(), 500); }}
            onSearch={() => load()} />
        </Col>
        <Col xs={12} sm={6}>
          <Select placeholder="Filtrar por tipo" allowClear style={{ width: '100%' }} value={filterType} onChange={setFilterType} getPopupContainer={t => t.parentNode}>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
          </Select>
        </Col>
        <Col xs={12} sm={8}>
          <DatePicker.RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder={['Desde', 'Hasta']} value={filterDates} onChange={setFilterDates} separator="→" />
        </Col>
      </Row>
      <Table dataSource={docs} columns={columns} rowKey="id" loading={loading} size="small"
        locale={{ emptyText: "Aún no has generado documentos" }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, showTotal: t => `${t} documentos`, onChange: p => load(p) }} />
    </>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MyDocuments = () => {
  const [selected, setSelected] = useState(null);
  const [form] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [currentDocId, setCurrentDocId] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const openCategory = (cat) => {
    setSelected(cat);
    setGeneratedUrl(null); setGeneratedPdfUrl(null); setCurrentDocId(null);
    form.resetFields();
    form.setFieldsValue({ date: dayjs(), startDate: dayjs(), movingDate: dayjs(), resignationDate: dayjs() });
  };

  const closeDrawer = () => {
    setSelected(null);
    setGeneratedUrl(null); setGeneratedPdfUrl(null); setCurrentDocId(null);
    form.resetFields();
  };

  const onGenerate = async (values) => {
    setGenerating(true);
    try {
      const data = serializeFormData(values);
      const { data: result } = await generateLegalDocument(selected.key, data, currentDocId);
      setGeneratedUrl(result.fileUrl);
      if (result.pdfUrl) setGeneratedPdfUrl(result.pdfUrl);
      if (!currentDocId) setCurrentDocId(result.id);
      message.success("Documento generado correctamente");
      setRefresh(r => r + 1);
    } catch (e) {
      message.error(e?.response?.data?.message || "Error al generar el documento");
    } finally { setGenerating(false); }
  };

  const ActiveForm = selected ? FORMS[selected.key] : null;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0, color: "#1d3557" }}>Mis Documentos</Title>
        <Text type="secondary">Genera documentos legales con los datos de tus clientes.</Text>
      </div>

      {/* Category groups */}
      <div style={{ marginBottom: 28 }}>
        {CATEGORY_GROUPS.map((group, gIdx) => (
          <div key={gIdx}>
            <Divider orientation="left" orientationMargin={0}
              style={{ color: "#555", fontSize: 12, fontWeight: 600, marginTop: gIdx === 0 ? 0 : 20, marginBottom: 10, borderColor: "#e8e8e8" }}>
              {group.group}
            </Divider>
            <Row gutter={[10, 10]}>
              {group.categories.map((cat) => (
                <Col xs={12} sm={8} md={6} lg={4} key={cat.key}>
                  <Card hoverable onClick={() => openCategory(cat)}
                    style={{ borderRadius: 10, border: `1.5px solid ${cat.border}`, background: cat.color, cursor: "pointer", textAlign: "center", position: "relative", height: "100%" }}
                    bodyStyle={{ padding: "14px 10px" }}>
                    <div style={{ marginBottom: 8 }}>{cat.icon}</div>
                    <Text strong style={{ fontSize: 12, display: "block", whiteSpace: "pre-line", lineHeight: 1.35 }}>
                      {cat.label}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 10, display: "block", marginTop: 4, lineHeight: 1.3 }}>
                      {cat.description}
                    </Text>
                    {cat.subtypes && cat.subtypes.length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                        {cat.subtypes.slice(0, 2).map(s => (
                          <Tag key={s.value} style={{ fontSize: 9, padding: "0 4px", margin: 0, lineHeight: "16px" }}>{s.label}</Tag>
                        ))}
                        {cat.subtypes.length > 2 && <Tag style={{ fontSize: 9, padding: "0 4px", margin: 0, lineHeight: "16px" }}>+{cat.subtypes.length - 2}</Tag>}
                      </div>
                    )}
                    <Button type="primary" size="small" icon={<PlusOutlined />}
                      style={{ marginTop: 10, background: "#1d3557", borderColor: "#1d3557", fontSize: 11 }}>
                      Generar
                    </Button>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ))}
      </div>

      {/* History */}
      <Card title={<><FileTextOutlined /> Documentos generados</>} style={{ borderRadius: 8 }}>
        <HistoryTable refresh={refresh} />
      </Card>

      {/* Drawer */}
      <Drawer open={!!selected} onClose={closeDrawer} width="min(860px, 100vw)" placement="right"
        closeIcon={<CloseOutlined style={{ color: "white", fontSize: 16 }} />}
        styles={{ body: { padding: "20px 24px", background: "#f0f2f5", overflowY: "auto" }, header: { background: "#1d3557", color: "white" } }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ filter: "brightness(0) invert(1)" }}>{selected?.icon}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "white" }}>{selected?.label?.replace("\n", " ")}</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", fontWeight: 400 }}>{selected?.description}</div>
            </div>
          </div>
        }
        extra={
          <Space>
            {generatedPdfUrl && (
              <Button icon={<DownloadOutlined />} href={generatedPdfUrl} target="_blank"
                style={{ background: "#c0392b", borderColor: "#c0392b", color: "white" }}>PDF</Button>
            )}
            {generatedUrl && (
              <Button icon={<DownloadOutlined />} href={generatedUrl} target="_blank"
                style={{ background: "#2d6a4f", borderColor: "#2d6a4f", color: "white" }}>Word</Button>
            )}
            <Button type="primary" icon={<FileTextOutlined />} loading={generating}
              onClick={() => form.submit()}
              style={{ background: "white", color: "#1d3557", borderColor: "white", fontWeight: 600 }}>
              {generating ? "Generando..." : "Generar documento"}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={onGenerate}>
          {ActiveForm && <ActiveForm form={form} />}
        </Form>
      </Drawer>
    </div>
  );
};

export default MyDocuments;
